import React from "react";

import { ApiProvider } from "../api/api-provider";
import {
  fetchLegacyWebsocketAccessToken,
  getLegacyFiinderApi,
  initWebSocket,
} from "../api/fiinder-api";
import type { FiinderApi } from "../api/fiinder-api-types";
import {
  clearWebsocketConfiguration,
  configureWebsocket,
} from "../api/websocket-config";
import { PersonationContext } from "@/lib/personation-context";
import {
  assignGlobals,
  toPersonationValue,
  type PersonationProps,
} from "@/components/providers/personation";

declare global {
  interface Window {
    FIINDER_API_URL?: string;
    FIINDER_COMPANY_TOKEN?: string;
    FIINDER_AGENT_NAME?: string;
    FIINDER_AGENT_ROLE?: string;
    FIINDER_AGENT_AVATAR_URL?: string;
    FIINDER_COMPANY_MAIN_COLOR?: string;
    FIINDER_CHAT_INTRO_MESSAGE?: string;
    FIINDER_CHAT_INTRO_META?: string;
    FIINDER_CHAT_START_QUICK_QUESTIONS_TITLE?: string;
    FIINDER_CHAT_START_QUICK_QUESTIONS?: string;
    FIINDER_CHAT_LAUNCHER_CALL_TO_ACTION?: string;
    FIINDER_AVATAR_SIZE?: number | string;
    FIINDER_LINK_TARGET?: "_blank" | "_self";
    FIINDER_FORCE_TOP_LAYER?: boolean | string;
    FIINDER_SEARCH_PLACEHOLDER?: string;
    FIINDER_RECENTLY_VISITED_TITLE?: string;
    FIINDER_LABELS?: Partial<{
      chat: Partial<{
        inputPlaceholder: string;
        connectionLost: string;
        reloadButton: string;
        followUpsTitle: string;
        introMessage: string;
        introMessageMeta: string;
        promptSuggestionsTitle: string;
        launcherCallToAction: string;
      }>;
      sources: Partial<{
        searching: string;
        reading: string;
        noSourcesAvailable: string;
        sourcesCount: string;
        sourcesTitle: string;
        showAll: string;
        siteCount: string;
        siteCountPlural: string;
      }>;
      search: Partial<{
        startTyping: string;
        noResults: string;
        searching: string;
        placeholder: string;
        recentlyVisitedTitle: string;
      }>;
      landing: Partial<{
        sidebar: Partial<{
          labels: Partial<{
            chat: string;
            search: string;
            history: string;
          }>;
        }>;
      }>;
    }>;
  }
}

type FiinderProviderProps = PersonationProps & {
  children: React.ReactNode;
};

export function FiinderProvider(props: FiinderProviderProps) {
  const personation = React.useMemo(
    () => toPersonationValue(props),
    [
      props.fiinderApiUrl,
      props.companyToken,
      props.agentName,
      props.agentRole,
      props.agentAvatarUrl,
      props.companyMainColor,
      props.introMessage,
      props.introMessageMeta,
      props.promptSuggestionsTitle,
      props.promptSuggestions,
      props.launcherCallToAction,
      props.launcherAvatarSize,
      props.linkTarget,
      props.forceTopLayer,
      props.searchPlaceholder,
    ]
  );

  const apiRef = React.useRef<{ api: FiinderApi; key: string } | null>(null);
  const apiKey = `${props.fiinderApiUrl}::${props.companyToken}`;

  if (apiRef.current === null || apiRef.current.key !== apiKey) {
    assignGlobals(props);
    configureWebsocket({
      async getCredentials() {
        const accessToken = await fetchLegacyWebsocketAccessToken();
        return { accessToken };
      },
    });
    const wsConnect = initWebSocket();
    apiRef.current = {
      api: getLegacyFiinderApi(wsConnect),
      key: apiKey,
    };
  }

  React.useEffect(() => {
    assignGlobals(props);
  }, [personation]);

  React.useEffect(() => {
    return () => {
      clearWebsocketConfiguration();
    };
  }, []);

  return (
    <PersonationContext.Provider value={personation}>
      <ApiProvider api={apiRef.current.api}>{props.children}</ApiProvider>
    </PersonationContext.Provider>
  );
}
